import { ExternalLink, Smartphone } from 'lucide-react';

import { useShopContent } from '@/features/storefront/content/useContent';
import { useInstallApp } from '@/features/storefront/pwa/installAppContext';
import { cn } from '@/lib/cn';

/**
 * The theme's "install this shop" button - the one line a theme's footer
 * renders, beside its language switcher or under its help column. Nothing in
 * the customiser's inert preview, on a plan without an installable storefront,
 * or once the shop is already on the shopper's home screen.
 *
 * Drawn in `currentColor` so it sits as naturally in an inverted footer as in
 * a light one.
 */
export function InstallAppSlot({
  preview,
  className,
}: {
  preview?: boolean;
  className?: string;
}) {
  const app = useInstallApp();
  const text = useShopContent();
  if (preview || !app?.available) return null;

  return (
    <div className={cn('flex', className)}>
      <button
        type="button"
        disabled={app.busy}
        onClick={() => void app.start()}
        className="inline-flex items-center gap-2 border border-current px-3 py-1.5 text-sm font-medium hover:opacity-70 disabled:opacity-60"
        style={{ borderRadius: 'var(--storefront-radius)' }}
      >
        {app.capability === 'open' ? (
          <ExternalLink className="size-4 shrink-0" aria-hidden />
        ) : (
          <Smartphone className="size-4 shrink-0" aria-hidden />
        )}
        {app.busy
          ? text('pwa.installing')
          : app.capability === 'open'
            ? text('pwa.open_app', { store: app.storeName })
            : text('pwa.install_store', { store: app.storeName })}
      </button>
    </div>
  );
}
